import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@material-ui/core';
import {
  Filter1Rounded,
  GetAppRounded,
  List,
} from '@material-ui/icons';
import React, { useRef, useState, useMemo } from 'react';
import { useUpdateEffect } from 'react-use';
import styled from 'styled-components/macro';
import {
  MAX_LENGTH_TO_DOWNLOAD_PER_STATUS,
  WorkdeskTickets,
} from 'store/models/workdesk-tickets/WorkdeskTickets';
import {
  forDownloadIsLoadingSelector,
  forDownloadTicketsSelector,
} from 'store/selectors/download.selector';
import { useDispatch, useSelector } from 'react-redux';
import { downloadActions } from 'store/actions';
import { CSVLink } from 'react-csv';
import loadingImg from 'assets/images/spinner.gif';
import moment from 'utils/app-moment';
import {
  selectedTicketTypeSelector,
  workdeskFilterSelectedStatusSelector,
} from 'store/selectors/workdesk-filter.selector';
import { Statuses } from 'store/models/Statuses';
import { MAX_LENGTH_TO_DOWNLOAD } from 'config/workdesk-load';

interface ExportDataProps {}

const ExportData: React.FC<ExportDataProps> = () => {
  const csvLinkDataRef = useRef<any>(null);
  const dispatch = useDispatch();
  const [showMenu, setShowMenu] = useState(null);
  const ticketType = useSelector(selectedTicketTypeSelector);

  const selectedStatus = useSelector(
    workdeskFilterSelectedStatusSelector,
  );

  const statusLabel = useMemo(
    () =>
      selectedStatus &&
      ticketType &&
      Statuses.getLabelOfStatus(selectedStatus, ticketType),
    [selectedStatus, ticketType],
  );

  const handleSetShowMenu = (e) => {
    setShowMenu(e.currentTarget);
  };
  const handleClose = () => {
    setShowMenu(null);
  };

  const [justClickedOption, setJustClickedOption] = useState<
    string | null
  >(null);

  const handleClickAllStatuses = () => {
    setJustClickedOption('all');
    dispatch(downloadActions.loadAllStatusesCsvWorkdeskTickets());
    handleClose();
  };

  const handleClickCurStatus = () => {
    setJustClickedOption(selectedStatus);
    dispatch(downloadActions.loadCurStatusCsvWorkdeskTickets());
    handleClose();
  };

  const isLoadingCsvData = useSelector(forDownloadIsLoadingSelector);
  const ticketsCsvData = useSelector(forDownloadTicketsSelector);

  const canDownloadAll = useMemo(
    () => Statuses.isReadyToViewStatus(selectedStatus),
    [selectedStatus],
  );

  /**
   * if use updateEffect on hot reload it will auto download file
   */
  useUpdateEffect(() => {
    if (!isLoadingCsvData && ticketsCsvData?.length) {
      // use set timeout to wait for the ref to be updated
      setTimeout(() => {
        csvLinkDataRef.current?.link?.click();
      }, 0);
    }
  }, [ticketsCsvData, isLoadingCsvData]);

  const csvFilename = useMemo(
    () =>
      `workdesks-data-${justClickedOption}_${moment().format(
        'YYYYMMDD_HHmm',
      )}.csv`,
    [justClickedOption],
  );

  const statusesLabelsText = useMemo(
    () =>
      Statuses.mainStatuses
        .map((status) =>
          Statuses.getLabelOfStatus(status, ticketType),
        )
        .join(', '),
    [ticketType],
  );
  return (
    <SCsvLinkWrapper>
      <Button
        variant="contained"
        color="primary"
        startIcon={
          isLoadingCsvData ? (
            <SLoadingImage src={loadingImg} width="25px" />
          ) : (
            <GetAppRounded />
          )
        }
        onClick={(e) => handleSetShowMenu(e)}
      >
        Export data (.csv)
      </Button>

      <Menu
        open={Boolean(showMenu)}
        anchorEl={showMenu}
        onClose={handleClose}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
      >
        {canDownloadAll && (
          <Tooltip
            title={
              <STooltipUl>
                <li>{`Include ${statusesLabelsText}`}</li>
                <li>{`Maximum number of rows to download per status is ${MAX_LENGTH_TO_DOWNLOAD_PER_STATUS} rows`}</li>
              </STooltipUl>
            }
          >
            <MenuItem onClick={() => handleClickAllStatuses()}>
              <ListItemIcon>
                <List />
              </ListItemIcon>

              <ListItemText>{`Export "All" Statuses`}</ListItemText>
            </MenuItem>
          </Tooltip>
        )}
        <Tooltip
          title={`Maximum number of rows to download is ${MAX_LENGTH_TO_DOWNLOAD} rows`}
        >
          <MenuItem onClick={() => handleClickCurStatus()}>
            <ListItemIcon>
              <Filter1Rounded />
            </ListItemIcon>

            <ListItemText>{`Export ${statusLabel}`}</ListItemText>
          </MenuItem>
        </Tooltip>
      </Menu>

      {/* hidden link for download data */}
      <CSVLink
        ref={(ref) => (csvLinkDataRef.current = ref)}
        data={ticketsCsvData}
        filename={csvFilename}
        enclosingCharacter={`"`}
        headers={WorkdeskTickets.headersForDownload}
      ></CSVLink>
    </SCsvLinkWrapper>
  );
};

export default ExportData;
const SCsvLinkWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
const SLoadingImage = styled.img``;
const STooltipUl = styled.ul`
  padding-left: 10px;
`;
